import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ModalController } from '@ionic/angular';
import { OpeningMessageModalComponent } from '@lib/shared/components/opening-message-modal/opening-message-modal.component';
import { AdminSettings } from '@lib/shared/models/admin-settings.model';
import { User } from '@lib/shared/models/user.model';
import { UserService } from '@lib/shared/services/user.service';
import { firstValueFrom } from 'rxjs';

import { AdminSettingsService } from './admin-settings.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class OpeningMessageService {
  private readonly postSignupStorageKey = 'ekc:post-signup-opening-message';

  constructor(
    private adminSettingsService: AdminSettingsService,
    private userService: UserService,
    private modalController: ModalController,
    private afs: AngularFirestore,
    private toastService: ToastService,
  ) {}

  setPendingForSignup(): void {
    localStorage.setItem(this.postSignupStorageKey, 'true');
  }

  clearPendingForSignup(): void {
    localStorage.removeItem(this.postSignupStorageKey);
  }

  isPendingForSignup(): boolean {
    return localStorage.getItem(this.postSignupStorageKey) === 'true';
  }

  async maybeShowAfterSignup(userId: string): Promise<void> {
    if (!this.isPendingForSignup() || !userId) {
      return;
    }

    try {
      const settingsSnapshot = await firstValueFrom(this.adminSettingsService.getSettings().get());
      const settings = settingsSnapshot.data() as AdminSettings;
      const openingMessage = settings?.openingMessage;

      if (!openingMessage?.enabled || !openingMessage?.bodyHtml?.trim()) {
        this.clearPendingForSignup();
        return;
      }

      const messageVersion = openingMessage.version || 1;
      const userSnapshot = await firstValueFrom(this.afs.doc<User>(`users/${userId}`).get());
      const userData = this.snapshotExists(userSnapshot) ? (userSnapshot.data() as User) : null;
      const seenVersion = userData?.openingMessageLastSeenVersion || 0;

      if (seenVersion >= messageVersion) {
        this.clearPendingForSignup();
        return;
      }

      const modal = await this.modalController.create({
        component: OpeningMessageModalComponent,
        backdropDismiss: false,
        componentProps: {
          title: openingMessage.title,
          bodyHtml: openingMessage.bodyHtml,
        },
      });

      await modal.present();
      await modal.onDidDismiss();

      await this.userService.updateUser(userId, {
        openingMessageLastSeenVersion: messageVersion,
        openingMessageLastSeenAt: new Date(),
      });

      this.clearPendingForSignup();
    } catch (error) {
      console.error(error);
      this.toastService.showDissmissableErrorToast('Unable to load opening message.');
    }
  }

  private snapshotExists(snapshot: any): boolean {
    return typeof snapshot?.exists === 'function' ? snapshot.exists() : Boolean(snapshot?.exists);
  }
}
